import {
  ConditionalTrialRouting,
  PreScreeningQuestion,
  QuestionType,
  RadioGridQuickSelect,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  DIABETES_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  INFECTIOUS_DISEASE_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Lower Leg Pain Management Study
 */
export const legPainScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "diabetesPresence",
    type: QuestionType.RADIO,
    question: "Do you have diabetes?",
    helpText: "This includes type 1 diabetes mellitus (T1DM) as well as type 2 diabetes mellitus (T2DM)",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No", disqualify: true },
    ],
    disqualifyMessage: "This study is only for people with established diabetes.",
    attributeMapping: mappers.value({
      yes: [],
      no: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "diabetesType1",
    type: QuestionType.RADIO,
    question: "Do you have type 1 diabetes?",
    helpText: "Type 1 diabetes is an autoimmune condition where the body attacks insulin-producing cells in the pancreas, resulting in little or no insulin production",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false }],
    }),
  },
  {
    id: "diabetesType2",
    type: QuestionType.RADIO,
    question: "Do you have type 2 diabetes?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes" },
      {
        value: "no",
        label: "No",
        disqualify: (allAnswers) => {
          // Disqualify only if they also said No to type 1
          return allAnswers.diabetesType1 === "no";
        },
      },
    ],
    disqualifyMessage: "This study requires participants with type 1 or type 2 diabetes.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false }],
    }),
  },
  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },
  {
    ...standardWeightQuestion,
    minBMI: 18,
    maxBMI: 40,
    minWeight: 99,
    helpText:
      "We'll calculate your BMI (Body Mass Index) using your height and weight.",
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },
  {
    id: "dpnDiagnosis",
    type: QuestionType.RADIO,
    question: "Do you have a diagnosis of diabetic peripheral neuropathy (DPN)?",
    helpText: "Diabetic peripheral neuropathy is nerve damage caused by your diabetes, and commonly presents as lower leg pain, numbness, tingling or weakness",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", skipNextQuestions: 1 },
      { value: "no", label: "No" },
    ],
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 24900, booleanAttributeValue: true }], // has_diabetic_neuropathy_diagnosis
      no: [{ attributeDefinitionId: 24900, booleanAttributeValue: false }],
    }),
  },
  {
    id: "dpnLegSymptoms",
    type: QuestionType.RADIO,
    question: "In both of your lower legs, do you have symptoms of pain, burning, tingling or numbness?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No", disqualify: true },
    ],
    disqualifyMessage: "This study requires participants who experience symptoms in both lower legs.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 24818, booleanAttributeValue: true }], // has_symptoms_peripheral_neuropathy_lower_limbs
      no: [{ attributeDefinitionId: 24818, booleanAttributeValue: false }],
    }),
  },
  {
    id: "dpnSymptomsDuration",
    type: QuestionType.RADIO,
    question: "Have you had symptoms of pain in both of your legs due for at least 9 months?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No", disqualify: true },
    ],
    disqualifyMessage: "This study requires participants who have had symptoms in both legs for at least 9 months.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 26464, booleanAttributeValue: true }], // has_symptoms_DPN_Painful_9_months
      no: [{ attributeDefinitionId: 26464, booleanAttributeValue: false }],
    }),
  },
  {
    id: "painSeverity",
    type: QuestionType.RADIO,
    question: "On a scale of 0 to 10, how severe is your lower leg pain on a weekly average?",
    helpText: "Please select the 'average amount of pain' that you feel in your lower legs over the course of a typical week",
    required: true,
    allowBack: true,
    options: [
      { value: "0", label: "(0) No pain at all", disqualify: true },
      { value: "1-3", label: "(1-3) Mild pain that's noticeable but doesn't stop me from doing daily activities - the pain does not have a negative impact on my life" },
      { value: "4-5", label: "(4-5) Moderate pain that's uncomfortable and distracting, preventing some daily activities" },
      { value: "6-7", label: "(6-7) Severe pain that interferes with concentration and limits most daily activities" },
      { value: "8-9", label: "(8-9) Very severe pain that is excruciating and limits all activities, often requiring immediate, urgent treatment" },
      { value: "10", label: "(10) Worst pain you can imagine", disqualify: true },
    ],
    disqualifyMessage: "Based on your pain severity, you do not qualify for this study.",
    attributeMapping: mappers.value({
      "0": [],
      "1-3": [],
      "4-5": [],
      "6-7": [],
      "8-9": [],
      "10": [],
    }),
  },
  {
    id: "opioidUseHistory",
    type: QuestionType.RADIO,
    question: "In the last 3 months, have you used opioid medications (opioid pain killers) for the management of chronic pain?",
    helpText: "Examples of opioids include OxyContin, Vicodin, MS Contin, Kadian, Duragesic patches, Sublimaze, Actiq lozenges, Dilaudid, Ultram, among others",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants who have used opioid medications in the last 3 months.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23788, booleanAttributeValue: true }], // has_used_opioids_chronic_pain_3_months
      no: [{ attributeDefinitionId: 23788, booleanAttributeValue: false }],
    }),
  },
  {
    id: "disabilityBenefits",
    type: QuestionType.RADIO,
    question: "Are you currently receiving disability benefits or actively pursuing a disability claim due to your symptoms of lower leg pain?",
    helpText: "This refers to disability benefits related to your condition of lower leg pain, numbness, tingling",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "This study cannot include participants who are receiving or pursuing disability benefits for lower leg pain.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 26067, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: 26067, booleanAttributeValue: false }],
    }),
  },
  {
    id: "medicalConditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "liver_failure",
        label: "Diagnosed with liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "cancer_5_years",
        label: "Diagnosed with cancer in the past 5 years (excluding SCC and BCC skin cancers)",
        disqualify: true,
      },
      {
        value: "uncontrolled_hiv",
        label: "Diagnosed with HIV that is advanced or not well controlled",
        disqualify: true,
      },
      {
        value: "hepatitis_b_c",
        label: "Diagnosed with active hepatitis B or hepatitis C",
        disqualify: true,
      },
      {
        value: "eskd_dialysis",
        label: "Diagnosed with end-stage kidney disease (ESKD) or currently receiving dialysis",
        disqualify: true,
      },
      {
        value: "cardiac_device",
        label: "Have an implanted cardiac device (e.g., a pacemaker or defibrillator)",
        disqualify: true,
      },
      {
        value: "lower_limb_amputation",
        label: "Have had a lower limb amputation (excluding toe amputations)",
        disqualify: true,
      },
      {
        value: "none_above",
        label: "None of the above",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        liver_failure: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true },
        ],
        cancer_5_years: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
        cardiac_device: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PACEMAKER, booleanAttributeValue: true },
        ],
        uncontrolled_hiv: [
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
        ],
        hepatitis_b_c: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: true },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: true },
        ],
        eskd_dialysis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
        ],
        lower_limb_amputation: [
          { attributeDefinitionId: 1109, booleanAttributeValue: true }, // had_amputation_lowerlimb
        ],
      },
      noneAttributeMap: {
        none_above: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: false },
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PACEMAKER, booleanAttributeValue: false },
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: 1109, booleanAttributeValue: false }, // had_amputation_lowerlimb
        ],
      },
    }),
  },
  {
    id: "cannabisUse",
    type: QuestionType.RADIO,
    question: "Are you currently using any inhaled or oral cannabis products?",
    helpText: "This includes any cannabis/marijuana, weed, pot, ganja, Mary Jane, bud, herb, chronic, dank, grass, reefer, skunk, dope, green, hash, or kush",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No", skipNextQuestions: 1 },
    ],
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23195, booleanAttributeValue: true }], // Cannabis_current
      no: [{ attributeDefinitionId: 23195, booleanAttributeValue: false }],
    }),
  },
  {
    id: "cannabisWillingnessToStop",
    type: QuestionType.RADIO,
    question: "Would you be willing to stop using cannabis products for the duration of the study?",
    helpText: "This includes any cannabis/marijuana, or tetrahydrocannabinol-containing (THC) medications or recreational substances",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No", disqualify: true },
    ],
    disqualifyMessage: "This study requires participants to stop using cannabis products for the duration of the study.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23185, booleanAttributeValue: true }], // Cannabis_willingtostop
      no: [{ attributeDefinitionId: 23185, booleanAttributeValue: false }],
    }),
  },
  {
    id: "painMedicationExclusions",
    type: QuestionType.RADIO_GRID,
    question: "Are you currently taking any of these medications?",
    required: true,
    allowBack: true,
    quickSelect: new RadioGridQuickSelect(
      "I am not taking any of these medications",
      "not_taking",
      "Submit my medications",
      {
        allText: "All medications in this group will be marked as 'not taking'",
        remainingText: "Remaining medications in this group will be marked as 'not taking'",
      },
      "_not_taking",
    ),
    gridLayout: {
      columnHeaders: ["Currently taking", "Not taking"],
      rows: [
        {
          label: "Pregabalin",
          sublabel: "Lyrica",
          values: [
            { value: "pregabalin_current", disqualify: true },
            { value: "pregabalin_not_taking" },
          ],
        },
        {
          label: "Gabapentin",
          sublabel: "Neurontin",
          values: [
            { value: "gabapentin_current", disqualify: true },
            { value: "gabapentin_not_taking" },
          ],
        },
        {
          label: "SNRI medications",
          sublabel: "Examples: Effexor (venlafaxine), Cymbalta (duloxetine), Pristiq (desvenlafaxine)",
          values: [
            { value: "snri_current", disqualify: true },
            { value: "snri_not_taking" },
          ],
        },
        {
          label: "Tricyclic antidepressants (TCAs)",
          sublabel: "Examples: Endep/Elavil (amitriptyline), Pamelor/Aventyl (nortriptyline), Norpramin (desipramine)",
          values: [
            { value: "tca_current", disqualify: true },
            { value: "tca_not_taking" },
          ],
        },
        {
          label: "Lamotrigine",
          sublabel: "Lamictal",
          values: [
            { value: "lamotrigine_current", disqualify: true },
            { value: "lamotrigine_not_taking" },
          ],
        },
        {
          label: "Capsaicin",
          sublabel: "Zostrix, Qutenza (patches or creams)",
          values: [
            { value: "capsaicin_current", disqualify: true },
            { value: "capsaicin_not_taking" },
          ],
        },
        {
          label: "Carbamazepine",
          sublabel: "Tegretol",
          values: [
            { value: "carbamazepine_current", disqualify: true },
            { value: "carbamazepine_not_taking" },
          ],
        },
        {
          label: "Oxcarbazepine",
          sublabel: "Trileptal",
          values: [
            { value: "oxcarbazepine_current", disqualify: true },
            { value: "oxcarbazepine_not_taking" },
          ],
        },
        {
          label: "Phenytoin",
          sublabel: "Dilantin",
          values: [
            { value: "phenytoin_current", disqualify: true },
            { value: "phenytoin_not_taking" },
          ],
        },
        {
          label: "Topiramate",
          sublabel: "Topamax, Topiragen, Epiramax",
          values: [
            { value: "topiramate_current", disqualify: true },
            { value: "topiramate_not_taking" },
          ],
        },
        {
          label: "Valproate",
          sublabel: "Epilim",
          values: [
            { value: "valproate_current", disqualify: true },
            { value: "valproate_not_taking" },
          ],
        },
      ],
    },
    disqualifyMessage: "This study cannot include participants who are currently taking these medications.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {},
    }),
  },
  {
    id: "currentTrial",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in another clinical trial for a medication, device or other intervention or treatment?",
    helpText: "If you are only involved in follow up (observational) activities, and you are no longer receiving the interventional medication/vaccination, answer 'No'",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "You can only be in one clinical trial at a time.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false }],
    }),
  },
];

/**
 * Trial routing for Lower Leg Pain studies
 *
 * Priority order:
 * 1. VDIAB48110
 * 2. VDIAB48111
 */
export const legPainTrialRouting: ConditionalTrialRouting = {
  getEligibleTrials: (_answers, age) => {
    if (age !== undefined && (age < 18 || age > 80)) {
      return [];
    }
    return ["VDIAB48110", "VDIAB48111"];
  },
};
